import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject, of } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators';
import { ConfirmService } from 'src/app/utils/confirm/confirm.service';
import { CreateLocationSizeModalComponent } from 'src/app/utils/modals/create-location-size-modal/create-location-size-modal.component';
import { CreatePickingZoneModalComponent } from 'src/app/utils/modals/create-picking-zone-modal/create-picking-zone-modal.component';
import { apiRoutes } from '../../../environments/api-routes';
import { TakeUntilDestroy } from '../../core/decorators/take-until-destroy';
import { List } from '../../core/models/list';
import { Conditions } from '../../core/services/list/models/FilterField';
import { Filters } from '../../core/services/list/models/Filters';
import { Order } from '../../core/services/list/models/Order';
import { Paginator } from '../../core/services/list/models/Paginator';
import { SortDirection } from '../../core/services/list/models/sort-direction.enum';
import { TextFilterField } from '../../core/services/list/models/TextFilterField';
import { AutocompleteModel } from '../../utils/autocomplete/autocomplete.model';
import { SelectModel } from '../../utils/miku-select/select.model';
import { LocationSize } from '../models/location-size.interface';
import { LocationInterface } from '../models/location.interface';
import { Location } from '../models/location.model';
import { PickingZone } from '../models/picking-zone.interface';
import { LocationType } from '../models/type.model';
import { LocationApiService } from './location-api.service';
import { DefaultLocation } from '../../PIM/models/defaultLocation.model';

@Injectable({
  providedIn: 'root',
})
@TakeUntilDestroy
export class LocationService implements OnDestroy {
  private serviceDestroyed = new Subject<void>();

  apiRoutes = apiRoutes;

  locations: Location[] = [];

  locationSizes: LocationSize[] = [];

  pickingZones: PickingZone[] = [];

  specialLocationsExcludedInHandlingPalletsModule: LocationInterface[] = [];

  defaultProducts: DefaultLocation[];

  constructor(
    private locationApiService: LocationApiService,
    private dialog: MatDialog,
    private toastService: ToastrService,
    private confirmService: ConfirmService,
  ) {}

  ngOnDestroy() {}

  getLocationList(initializedFilters: Filters, deleted = false) {
    return this.locationApiService.mikuGetLocations(initializedFilters, deleted).pipe(
      tap(({ items }: List<Location>) => {
        this.locations = items;
        this.locations.map(location => (location.massAction = false));
      }),
      takeUntil(this.serviceDestroyed),
      catchError(e => of(e)),
    );
  }

  getLocationSelectList() {
    return this.locationApiService
      .getLocationTypes(new Filters([], new Order(['name', SortDirection.ASC], []), new Paginator(1, 9999)))
      .pipe(
        map(({ items }: List<LocationType>) => [
          ...items.map((el: LocationType) => new SelectModel<LocationType>(el, el.name)),
        ]),
        shareReplay(),
        takeUntil(this.serviceDestroyed),
        catchError(e => of(e)),
      );
  }

  locationsAutocompleteList(query): Observable<AutocompleteModel[]> {
    return this.locationApiService
      .mikuGetLocations(
        new Filters(
          [new TextFilterField('barcode', 'Barcode', query).setCondition(Conditions.LIKE)],
          new Order(['barcode', SortDirection.ASC], []),
          new Paginator(1, 50),
        ),
      )
      .pipe(
        map(({ items }): AutocompleteModel[] => [
          new AutocompleteModel(null, 'Any'),
          ...items.map((el: Location) => new AutocompleteModel(el.barcode, el.barcode)),
        ]),
        shareReplay(),
        catchError(e => of(e)),
      );
  }

  getLocationByBarcode(barcode: string): Observable<Location> {
    return this.locationApiService.getLocationByBarcode(barcode).pipe(
      takeUntil(this.serviceDestroyed),
      catchError(e => of(e)),
    );
  }

  getIsMergeLocation(): Observable<List<LocationInterface>> {
    return this.locationApiService.getIsMergeLocation().pipe(
      tap(locations => (this.specialLocationsExcludedInHandlingPalletsModule = locations.items)),
      takeUntil(this.serviceDestroyed),
    );
  }

  getLocationSizes(): Observable<List<LocationSize>> {
    return this.locationApiService.getLocationSizes().pipe(
      tap(({ items }: List<LocationSize>) => (this.locationSizes = items)),
      takeUntil(this.serviceDestroyed),
    );
  }

  addLocationSize(): void {
    const dialogRef = this.dialog.open(CreateLocationSizeModalComponent, {
      width: 'auto',
      minWidth: '300px',
      maxWidth: '500px',
      maxHeight: '90vh',
    });

    dialogRef.componentInstance.onApprove
      .pipe(
        switchMap(locationSize => this.locationApiService.createLocationSize(locationSize)),
        tap(() => this.toastService.success('Location size created successfully')),
        switchMap(() => this.getLocationSizes()),
        takeUntil(this.serviceDestroyed),
      )
      .subscribe();
  }

  deleteLocationSize(locationSize: LocationSize): Observable<List<LocationSize>> {
    return this.confirmService.show('Do you really want to delete this location size?').pipe(
      filter(decision => decision),
      switchMap(() => this.locationApiService.deleteLocationSize(locationSize?.id)),
      switchMap(() => this.getLocationSizes()),
      tap(() => this.toastService.success('Location size deleted successfully')),
      takeUntil(this.serviceDestroyed),
    );
  }

  editLocationSize(locationSize: LocationSize): void {
    const dialogRef = this.dialog.open(CreateLocationSizeModalComponent, {
      width: 'auto',
      minWidth: '300px',
      maxWidth: '500px',
      maxHeight: '90vh',
      data: locationSize,
    });

    dialogRef.componentInstance.onApprove
      .pipe(
        switchMap(payload => this.locationApiService.editLocationSize(locationSize.id, payload)),
        tap(() => this.toastService.success('Location size updated successfully')),
        switchMap(() => this.getLocationSizes()),
        takeUntil(this.serviceDestroyed),
      )
      .subscribe();
  }

  getPickingZones(): Observable<List<PickingZone>> {
    return this.locationApiService.getPickingZones().pipe(
      tap(({ items }: List<PickingZone>) => (this.pickingZones = items)),
      takeUntil(this.serviceDestroyed),
    );
  }

  getPickingZoneSelectList() {
    return this.locationApiService.getPickingZones().pipe(
      map(({ items }: List<PickingZone>) => [
        ...items.map((el: PickingZone) => new SelectModel<PickingZone>(el, el.name)),
      ]),
      shareReplay(),
      takeUntil(this.serviceDestroyed),
      catchError(e => of(e)),
    );
  }

  addPickingZone(): void {
    const dialogRef = this.dialog.open(CreatePickingZoneModalComponent, {
      width: 'auto',
      minWidth: '400px',
      maxWidth: '500px',
      maxHeight: '90vh',
    });

    dialogRef.componentInstance.onApprove
      .pipe(
        switchMap(payload => this.locationApiService.createPickingZone(payload)),
        tap(() => this.toastService.success('Picking zone created successfully')),
        switchMap(() => this.getPickingZones()),
        takeUntil(this.serviceDestroyed),
      )
      .subscribe();
  }

  editPickingZone(id: number, pickingZone: PickingZone): void {
    const dialogRef = this.dialog.open(CreatePickingZoneModalComponent, {
      width: 'auto',
      minWidth: '300px',
      maxWidth: '500px',
      maxHeight: '90vh',
      data: pickingZone,
    });

    dialogRef.componentInstance.onApprove
      .pipe(
        switchMap(payload => this.locationApiService.editPickingZone(id, payload)),
        tap(() => this.toastService.success('Picking zone updated successfully')),
        switchMap(() => this.getPickingZones()),
        takeUntil(this.serviceDestroyed),
      )
      .subscribe();
  }

  deletePickingZone(id: number): Observable<List<PickingZone>> {
    return this.confirmService.show('Do you really want to delete this picking zone?').pipe(
      filter(decision => decision),
      switchMap(() => this.locationApiService.deletePickingZone(id)),
      switchMap(() => this.getPickingZones()),
      tap(() => this.toastService.success('Picking zone deleted successfully')),
      takeUntil(this.serviceDestroyed),
    );
  }

  getLocationSizeByPickingZoneId(
    pickingZoneId: number,
  ): Observable<{ pickingZone: PickingZone; locationSize: LocationSize }> {
    return this.getPickingZones().pipe(
      switchMap(() => this.getLocationSizes()),
      switchMap(() => {
        const currentPickingZone = this.pickingZones.find(pickingZone => pickingZone.id === pickingZoneId);
        const currentLocationSize = this.locationSizes.find(
          locationSize => locationSize.id === currentPickingZone.locationSize.id,
        );
        return of({ pickingZone: currentPickingZone, locationSize: currentLocationSize });
      }),
    );
  }

  getLocationDefaultProducts(locationId: number): Observable<any> {
    return this.locationApiService
      .getLocationDefaultProducts(locationId, {
        includes: 'replenishmentLevel,capacities',
      })
      .pipe(
        map(({ items }: List<DefaultLocation>) => this.mapLocationMaximumQuantity(items)),
        tap(items => (this.defaultProducts = items)),
      );
  }

  mapLocationMaximumQuantity(items: DefaultLocation[]): DefaultLocation[] {
    return items.map(item => ({
      ...item,
      location: {
        ...item.location,
        maxFillPercentage: item.capacities?.items?.find(capacity => capacity.product.id === item.product.id)
          ?.maxFillPercentage,
        maximumQuantity: item.capacities?.items?.find(capacity => capacity.product.id === item.product.id)?.sizeCapacity
          ?.capacity,
      },
    }));
  }
}
